import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Grid, Link, Typography} from '@mui/material';
import {useField} from 'formik';
import _, {clone, cloneDeep, flatten, groupBy, reduce, uniqBy} from 'lodash';
import {useSnackbar} from 'notistack';
import {useAccountContext} from '../../../contexts/AccountContext';
import {
  AutoFieldProps,
  ActionType,
  AutoField,
  SelectAutocompleteField,
  SelectOption,
  OptionValueType,
} from '../../../components/AutoForm/AutoFieldNew';
import Loading from '../../../components/Loading/Loading';

import {useRecommendContext} from './CreateRecommendHelpers';

import {PotDetails} from '../../../schema-types/PotDetails';
import {themeColors} from '../../../theme/Theme';
import {FeeFragment} from '../../../schema-types/FeeFragment';
import {OVPot} from '../../../schema-types/OVPot';
import {PotInfo, getPotInfo} from '../../../utils/getPotInfo';
import {useAutoField, useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';
import {recommendFieldsType} from './recommendFields';
import {NonStandardFeeCheck} from './useFeeValidation';
import saltus from '../../../shared/logger';
import {useAvailableFees} from './useAvailableFees';
import {SaltusAny} from '../../../utils/SaltusAny';
import useDjango from '../../../hooks/useDjango';

export type ongoingFeesProps = {
  formikKeyPrefixWithoutFee: string;
  inModify: boolean;
};

export const PlanFeesField: React.FC<
  ongoingFeesProps & {
    productId?: string | null;
    pot?: PotDetails | null;
    providerId?: string | null;
    mandateId?: string | null;
    inModify?: boolean;
    requiresMandate: boolean;
    hasSubplan: boolean;
    parentProductId?: string | null;
    isSaltus?: boolean;
  } & Partial<AutoFieldProps>
> = ({
  formikKeyPrefixWithoutFee,
  disabled,
  helperText,
  hasSubplan,
  productId,
  providerId,
  parentProductId,
  mandateId,
  requiresMandate,
  inModify,
  pot,
  isSaltus,
}) => {
  const {isSPPDA, isSPPAR} = useRecommendContext();
  const isSPP = isSPPDA || isSPPAR;
  const {values} = useAutoForm<recommendFieldsType>();
  // How this works:
  // the fee field is now JUST used for loading up server values into the form.
  // Then this is converted into either initialFixedFee or initialPercentageFee
  // The frontend then needs to convert them back to the values.
  // const ongoingFeeKey = `${formikKeyPrefixWithoutFee}.ongoingSimpleFee`;
  const {account} = useAccountContext();

  // turning off fee deviation if modify and model potfolio is type 'OEICS'
  const modelPortfolioType: string =
    (values.modify?.model as SaltusAny)?.providerContactJobTitle ?? '';
  const isOEICS = modelPortfolioType.toLocaleLowerCase().includes('oeics');
  const showDeviationFee = !(isOEICS && inModify);

  if (!account) {
    return (
      <Grid container spacing={2}>
        <Loading entityName="fees" />
      </Grid>
    );
  }
  const potInfo = pot ? getPotInfo(pot, values.id) : undefined;

  if (pot?.curoId || potInfo === PotInfo.existingNewPotByOtherRec) {
    if (!inModify) {
      return (
        <Box
          borderRadius="2px"
          border={`1px solid ${themeColors.greyLight}`}
          p={1.5}
          mt={0.5}
          pl={2}
        >
          <Typography variant="body1">Ongoing Fees already set</Typography>
        </Box>
      );
    }
  }

  if (!productId || !providerId || (requiresMandate && !mandateId)) {
    return (
      <Box
        pb={2.5}
        borderRadius="2px"
        border={`2px solid ${themeColors.yellow}`}
        p={2}
        mt={2}
        pl={2}
        boxShadow={2}
      >
        <Typography mb={0.5} variant="body1">
          <strong>Ongoing Fees:</strong>
        </Typography>
        {!productId || !providerId ? (
          <Typography>Please select a product</Typography>
        ) : null}
        {requiresMandate && !mandateId ? (
          <Typography>Please select a mandate</Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      pb={2.5}
      boxShadow={2}
      borderRadius="2px"
      mt={2}
      p={2}
      flex={1}
      border={`2px solid ${themeColors.lightBlue}`}
    >
      <Box display="flex" justifyContent="initial" alignItems="center">
        <Typography variant="body1">
          <strong>Set ongoing Fees:</strong>
        </Typography>
        {isSPP || !showDeviationFee ? null : (
          <Box display="flex" flexDirection="row" alignItems="center">
            <AutoField
              field={{
                id: `${formikKeyPrefixWithoutFee}.planFees.feeDeviation`,
                label: 'I have a fee deviation',
                size: 'small',
                type: ActionType.checkboxToggle,
              }}
            />
            <Link
              href="https://saltus.freshdesk.com/support/solutions/articles/201000120827-saltus-fees-2025"
              target="_blank"
              rel="noreferrer"
              variant="caption"
              sx={{ml: 1}}
            >
              Saltus Fees 2025
            </Link>
          </Box>
        )}
      </Box>
      <PlanFeesFieldInner
        formikKeyPrefixWithoutFee={formikKeyPrefixWithoutFee}
        disabled={disabled}
        productId={productId}
        providerId={providerId}
        mandateId={mandateId}
        hasSubplan={hasSubplan}
        parentProductId={parentProductId}
        inModify={inModify}
        isSaltus={isSaltus}
      />
    </Box>
  );
};

function sortFixedFees(a: feeExtendedType, b: feeExtendedType) {
  if (a.name?.includes('£') && b.name?.includes('£')) {
    if (a.amount && b.amount) {
      return Number(a.amount) > Number(b.amount) ? 1 : -1;
    }
  } else if (a.name?.startsWith('£')) {
    return -1;
  } else if (b.name?.startsWith('£')) {
    return 1;
  }
  if (!a.name) {
    return -1;
  }
  if (!b.name) {
    return 1;
  }
  return 0;
}
function sortPercentageFees(a: feeExtendedType, b: feeExtendedType) {
  if (!a.name) {
    return -1;
  }
  if (!b.name) {
    return 1;
  }
  return a.name > b.name ? 1 : -1;
}

export function feesToOptions(
  calcFees: feeExtendedType[] | null,
  isSaltus?: boolean
): SelectOption<OptionValueType>[] {
  if (!calcFees) {
    return [];
  }

  const groups = groupBy(calcFees, 'calculationBasis');

  const flattenedGroups: (SelectOption & {group: string} & feeExtendedType)[] = flatten(
    _.values(groups)
  ).map((option) => {
    let name = option.name!;
    let group = option.calculationBasis;
    if (option.calculationBasis === 'Fixed fee') {
      name = `${option.name} (£${option.amount})`;
    }
    if (option.calculationBasis === 'Percentage fee') {
      // eslint-disable-next-line eqeqeq
      // const allTiersEqual =
      //   !option.tiers || option.tiers?.every((v) => v === option.tiers?.[0]);
      // name = `${option.name} ${allTiersEqual ? `- ${option.annualPercentage}%` : ``}`;
      name = option.name || '';
    }

    if (option.used && option.calculated) {
      group = `Oneview calculated & used ${group}s`;
    } else if (option.used) {
      group = `Existing plan ${group}s`;
    } else if (option.deviation) {
      group = `Other ${group}s`;
    } else if (option.calculated) {
      group = `Oneview calculated ${group}s`;
    } else {
      group = `Other ${group}s`;
    }

    return {
      ...option,
      value: option.id!,
      label: name,
      group,
    };
  });

  const sortOrders: string[] = [
    'Existing and oneview calculated',
    'Oneview calculated',
    'Existing plan',
    'Other',
  ];
  const sortedOptions = flattenedGroups.sort((a, b) => {
    const aCategoryIndex = sortOrders.findIndex((category) => a.group.includes(category));
    const bCategoryIndex = sortOrders.findIndex((category) => b.group.includes(category));
    if (aCategoryIndex === bCategoryIndex) {
      if (a.calculationBasis === 'Fixed fee' && b.calculationBasis === 'Fixed fee') {
        return sortFixedFees(a, b);
      }
      if (
        a.calculationBasis === 'Percentage fee' &&
        b.calculationBasis === 'Percentage fee'
      ) {
        return sortPercentageFees(a, b);
      }
      if (a.calculationBasis === 'Fixed fee') {
        return -1;
      }
      if (b.calculationBasis === 'Fixed fee') {
        return 1;
      }
    } else {
      return aCategoryIndex > bCategoryIndex ? 1 : -1;
    }
    return 0;
  });

  return sortedOptions.filter((option) => {
    // If the provider is non saltus, dont include 'existing plan fees' on the FP dropdown
    if (!isSaltus) {
      return !(
        option.group?.toLocaleLowerCase().includes('existing plan') &&
        option.feeName?.name?.toLocaleLowerCase().includes('fp fee')
      );
    }
    return true;
  });
}

export type feeExtendedType = FeeFragment & {
  calculated?: boolean;
  deviation?: boolean;
  used?: boolean;
};

function mergeFees(
  calc: FeeFragment[],
  used: FeeFragment[] | undefined | null,
  deviated: FeeFragment[] | undefined | null,
  hasSubplan: boolean,
  inModify: boolean,
  feeDeviation: boolean
) {
  const calcFormatted: feeExtendedType[] = calc.map((x) => ({
    ...x,
    calculated: true,
  }));
  // if not a wrapper, also include previous plan fees
  // however in modify we need to see these as well
  const usedFormatted: feeExtendedType[] =
    hasSubplan && !inModify ? [] : used?.map((x) => ({...x, used: true})) || [];
  const deviatedFormatted: feeExtendedType[] =
    deviated?.map((x) => ({
      ...x,
      deviation: true,
    })) || [];
  const calcAndUsedId = uniqBy([...calcFormatted, ...usedFormatted], 'id');

  const calcAndUsed = reduce<feeExtendedType, feeExtendedType[]>(
    calcAndUsedId,
    (acc, curr) => {
      if (calc.some((x) => x.id === curr.id)) {
        curr.calculated = true;
      }
      if (used?.some((x) => x.id === curr.id)) {
        curr.used = true;
      }
      acc.push(curr);
      return acc;
    },
    []
  );
  saltus.log({calcAndUsed});
  if (feeDeviation) {
    return uniqBy([...calcAndUsed, ...deviatedFormatted], 'id');
  }
  return calcAndUsed;
}

type availableFeesExtendedType = {
  fees: feeExtendedType[];
  required: boolean | null;
};

export type feeDeciderExtendedType = {
  fpFees: availableFeesExtendedType;
  dfmFees: availableFeesExtendedType;
  productFees: availableFeesExtendedType;
};

export const PlanFeesFieldInner: React.FC<
  ongoingFeesProps & {
    productId: string;
    providerId: string;
    mandateId?: string | null;
    hasSubplan: boolean;
    parentProductId?: string | null;
    pot?: PotDetails | null;
    isSaltus?: boolean;
  } & Partial<AutoFieldProps>
> = ({
  formikKeyPrefixWithoutFee: formikKeyPrefix,
  disabled = false,
  helperText,
  productId,
  mandateId,
  providerId,
  parentProductId,
  hasSubplan,
  inModify,
  pot,
  isSaltus,
}) => {
  // How this works:
  // the fee field is now JUST used for loading up server values into the form.
  // Then this is converted into either initialFixedFee or initialPercentageFee
  // The frontend then needs to convert them back to the values.
  const {recommendDetails, isSPPDA} = useRecommendContext();
  const {enqueueSnackbar} = useSnackbar();
  const planFeeKey = `${formikKeyPrefix}.planFees`;
  const oldOngoingFeeKey = `${formikKeyPrefix}.planServerFee`;
  const DFMFeeIdKey = `${planFeeKey}.DFMFeeId`;
  const productFeeIdKey = `${planFeeKey}.productFeeId`;
  const FPFeeIdKey = `${planFeeKey}.FPFeeId`;
  const DFMFeeRequiredKey = `${planFeeKey}.DFMFeeRequired`;
  const productFeeRequiredKey = `${planFeeKey}.productFeeRequired`;
  const FPFeeRequiredKey = `${planFeeKey}.FPFeeRequired`;
  const feeDeviationKey = `${planFeeKey}.feeDeviation`;
  const [productFeeRequiredField, __, productFeeRequiredHelpers] =
    useAutoField<planFeesFieldType['productFeeRequired']>(productFeeRequiredKey);
  const [FPFeeRequiredField, ___, FPFeeRequiredHelpers] =
    useAutoField<planFeesFieldType['FPFeeRequired']>(FPFeeRequiredKey);
  const [DFMFeeRequiredField, ____, DFMFeeRequiredHelpers] =
    useAutoField<planFeesFieldType['DFMFeeRequired']>(DFMFeeRequiredKey);
  const [productFeeIdField, _____, {setValue: setProductFeeId}] = useAutoField<
    string | null
  >(productFeeIdKey);
  const [FPFeeIdField, ______, {setValue: setFPFeeId}] = useAutoField<string | null>(
    FPFeeIdKey
  );
  const [DFMFeeIdField, _______, {setValue: setDFMFeeId}] = useAutoField<string | null>(
    DFMFeeIdKey
  );

  const [{value: feeDeviation}, ________, {setValue: setFeeDeviationValue}] =
    useAutoField<boolean>(feeDeviationKey);
  const {account} = useAccountContext();
  const {data, feesLoading, getAvailableFees} = useAvailableFees();
  // The fee query is fetched lazily (network-only) and lags behind a mandate /
  // product change, so `data` briefly still holds the PREVIOUS selection's fees.
  // Track which selection the currently-loaded fees belong to, and only let the
  // auto-select effect below act once they match the current selection — otherwise
  // it re-derives a fee from the stale list and silently re-applies the fee a
  // mandate switch just cleared (ON-9247).
  // NOTE: keep this block in sync with BuilderPlanFees.tsx, which carries the same fix.
  const requestedFeesKey = `${productId}|${providerId}|${parentProductId}|${mandateId}`;
  const requestedFeesKeyRef = useRef(requestedFeesKey);
  requestedFeesKeyRef.current = requestedFeesKey;
  const [loadedFeesKey, setLoadedFeesKey] = useState<string | null>(null);

  useEffect(() => {
    const accountId = account?.id;
    if (disabled || !productId || !providerId || !accountId) {
      return;
    }
    getAvailableFees({
      variables: {
        productId,
        mandateId,
        providerId,
        parentProductId,
        accountId,
        hasSubplan,
      },
    });
  }, [
    hasSubplan,
    productId,
    mandateId,
    providerId,
    parentProductId,
    getAvailableFees,
    disabled,
    account?.id,
  ]);

  // `data` (network-only) only ever advances to the most-recently-requested
  // variables' result, so when it changes the loaded fees belong to the latest key.
  useEffect(() => {
    if (data?.availableFeeComponents) {
      setLoadedFeesKey(requestedFeesKeyRef.current);
    }
  }, [data]);

  // True only once the loaded fees correspond to the current product / mandate.
  const feesMatchMandate = loadedFeesKey === requestedFeesKey;

  const fees = useMemo<feeDeciderExtendedType | null>(() => {
    if (data?.availableFeeComponents) {
      const allFees = cloneDeep(data?.availableFeeComponents);

      allFees.dfmFees.fees = mergeFees(
        allFees.dfmFees.fees,
        isSPPDA ? [] : data.planFees?.dfmFees?.fees,
        data.feeComponents?.dfmFees.fees,
        hasSubplan,
        inModify,
        feeDeviation
      );

      allFees.fpFees.fees = mergeFees(
        allFees.fpFees.fees,
        isSPPDA ? [] : data.planFees?.fpFees?.fees,
        data.feeComponents?.fpFees.fees,
        hasSubplan,
        inModify,
        feeDeviation
      );

      allFees.productFees.fees = mergeFees(
        allFees.productFees.fees,
        isSPPDA ? [] : data.planFees?.productFees?.fees,
        data.feeComponents?.productFees.fees,
        hasSubplan,
        inModify,
        feeDeviation
      );

      return allFees;
    }
    return null;
  }, [feeDeviation, data, pot?.curoId, hasSubplan, inModify]);

  useEffect(() => {
    if (data?.availableFeeComponents) {
      if (!inModify) {
        productFeeRequiredHelpers.setValue(
          UPDATES._4010,
          data.availableFeeComponents.productFees?.required ?? null
        );
        FPFeeRequiredHelpers.setValue(
          UPDATES._4011,
          data.availableFeeComponents.fpFees?.required ?? null
        );
      }
      DFMFeeRequiredHelpers.setValue(
        UPDATES._4012,
        data.availableFeeComponents.dfmFees?.required ?? null
      );
    }
  }, [data?.availableFeeComponents, inModify, isSPPDA]);

  useEffect(() => {
    if (recommendDetails?.pots?.length) {
      // Only auto-set fee deviation if it's explicitly marked as true in the backend data
      // Don't auto-detect deviations based on mismatches between current fees and available fees
      // as this can lead to false positives when fees were previously valid but are no longer
      // in the current available fees list
      const hasExplicitFeeDeviation = recommendDetails?.pots?.some(
        (recPot) => recPot.hasFeeDeviation
      );

      if (hasExplicitFeeDeviation) {
        setFeeDeviationValue(UPDATES._4013, true, true);
      }
    }
  }, [setFeeDeviationValue, recommendDetails]);

  const feeFields = useMemo(() => {
    saltus.log({planFees: data?.planFees});
    if (fees) {
      const fields: {
        DFM: SelectAutocompleteField<any>;
        FP: SelectAutocompleteField<any>;
        product: SelectAutocompleteField<any>;
      } = {
        DFM: {
          id: DFMFeeIdKey,
          label: 'DFM Fee',
          type: ActionType.selectAutocomplete,
          disabled,
          options: feesToOptions(fees.dfmFees?.fees),
        },
        product: {
          id: productFeeIdKey,
          label: 'Product Fee',
          type: ActionType.selectAutocomplete,
          disabled,
          options: feesToOptions(fees.productFees?.fees),
        },
        FP: {
          id: FPFeeIdKey,
          label: 'FP Fee',
          type: ActionType.selectAutocomplete,
          disabled,
          options: feesToOptions(fees.fpFees?.fees, isSaltus),
        },
      };
      return fields;
    }
    return null;
  }, [fees, data?.planFees, disabled, hasSubplan, isSaltus]);

  useEffect(() => {
    // used to auto select if there is only one. Gate on feesMatchMandate so we never
    // auto-select against a previous mandate's fees while the new ones are loading —
    // that would silently re-apply the stale fee a mandate switch just cleared (ON-9247).
    if (fees && feesMatchMandate) {
      if (productFeeIdField.value || FPFeeIdField.value || DFMFeeIdField.value) {
        return;
      }
      const calcDFM = fees.dfmFees.fees.filter((x) => x.calculated);
      if (calcDFM.length === 1) {
        setDFMFeeId(UPDATES._4014, calcDFM[0].id);
      }
      const calcProduct = fees.productFees.fees.filter((x) => x.calculated);
      if (calcProduct.length === 1) {
        setProductFeeId(UPDATES._4015, calcProduct[0].id);
      }
      const calcFP = fees.fpFees.fees.filter((x) => x.calculated);
      if (calcFP.length === 1) {
        setFPFeeId(UPDATES._4016, calcFP[0].id);
      }
    }
  }, [
    fees,
    feesMatchMandate,
    productFeeIdField.value,
    FPFeeIdField.value,
    DFMFeeIdField.value,
    isSPPDA,
  ]);

  const selectedProductFee = fees?.productFees?.fees?.find(
    (x) => x?.id === productFeeIdField.value
  );
  const selectedDFMFee = fees?.dfmFees?.fees?.find((x) => x?.id === DFMFeeIdField.value);
  const selectedFPFee = fees?.fpFees?.fees?.find((x) => x?.id === FPFeeIdField.value);
  const [ongoingFee] = useField<string | undefined>(oldOngoingFeeKey);

  // code explanation here: https://github.com/Saltus-Partners/oneview/pull/3813
  const hideDFMFeeRequired =
    selectedDFMFee?.curoId === '5fba093a-770c-ea11-80eb-005056acd46f';
  const {djangoUser} = useDjango();
  const isDevTester =
    djangoUser?.isGod || djangoUser?.isUserDev || djangoUser?.isUserTester;
  // useEffect(() => {
  //   if (!hideDFMFeeRequired || !isDevTester) {
  //     return;
  //   }
  //   enqueueSnackbar({
  //     message: 'Note: OneView is setting zero rated DFM fee behind the scenes',
  //     variant: 'info',
  //   });
  // }, [hideDFMFeeRequired, isDevTester]);

  if (!feeFields) {
    if (disabled) {
      return null;
    }
    return <Loading />;
  }

  if (feesLoading) {
    return <Loading entityName="fees" />;
  }
  if (!fees) {
    return <div>Unable to show fees</div>;
  }

  return (
    <>
      {ongoingFee.value ? (
        <Typography>
          There is currently an ongoing fee of {ongoingFee.value} attached to this plan
        </Typography>
      ) : null}
      <Box borderBottom={`1px solid ${themeColors.white}`}>
        <Grid container alignItems="center" columnSpacing={1} pt={2} pb={2}>
          <Grid item xs={6}>
            {DFMFeeRequiredField.value && !hideDFMFeeRequired ? (
              <AutoField field={feeFields.DFM} disabled={disabled} />
            ) : null}
            {DFMFeeRequiredField.value && !hideDFMFeeRequired ? null : (
              <Typography pl={1.5}>No DFM Fee required</Typography>
            )}
          </Grid>
          <Grid className="calculationBasis" item xs textAlign="center">
            {DFMFeeRequiredField.value && !hideDFMFeeRequired ? (
              <Tiers fee={selectedDFMFee} />
            ) : null}
          </Grid>
        </Grid>
      </Box>
      <Box borderBottom={`1px solid ${themeColors.white}`}>
        <Grid container alignItems="center" columnSpacing={1} pt={2} pb={2}>
          <Grid item xs={6}>
            {FPFeeRequiredField.value ? (
              <AutoField field={feeFields.FP} disabled={disabled} />
            ) : null}
            {FPFeeRequiredField.value || inModify ? null : (
              <Typography pl={1.5}>No FP Fee required</Typography>
            )}
          </Grid>
          <Grid className="calculationBasis" item xs textAlign="center">
            {FPFeeRequiredField.value ? <Tiers fee={selectedFPFee} /> : null}
          </Grid>
        </Grid>
      </Box>
      <Box borderBottom={`1px solid ${themeColors.white}`}>
        <Grid container alignItems="center" columnSpacing={1} pt={2} pb={2}>
          <Grid item xs={6}>
            {productFeeRequiredField.value ? (
              <AutoField field={feeFields.product} disabled={disabled} />
            ) : null}
            {productFeeRequiredField.value || inModify ? null : (
              <Typography pl={1.5}>Product fee determined by provider</Typography>
            )}
          </Grid>
          <Grid className="calculationBasis" item xs textAlign="center">
            {productFeeRequiredField.value ? <Tiers fee={selectedProductFee} /> : null}
          </Grid>
        </Grid>
      </Box>
      {helperText?.length && <Typography variant="caption">{helperText}</Typography>}
      <NonStandardFeeCheck fees={fees} formikKeyPrefixWithoutFees={formikKeyPrefix} />
    </>
  );
};

export const Tiers: React.FC<{fee?: FeeFragment | null}> = ({fee}) => {
  if (!fee) {
    return null;
  }
  if (!fee.calculationBasis?.toLocaleLowerCase()?.includes('percentage')) {
    return (
      <Box>
        Flat rate - {fee.frequency} £{fee.amount}
      </Box>
    );
  }
  if (!fee.tiers) {
    return (
      <TiersDisplay
        fees={[
          fee.annualPercentage,
          fee.annualPercentage,
          fee.annualPercentage,
          fee.annualPercentage,
        ]}
      />
    );
  }
  return <TiersDisplay fees={[fee.tiers[0], fee.tiers[1], fee.tiers[2], fee.tiers[3]]} />;
};

export const TiersDisplay: React.FC<{
  fees: [
    string | number | null,
    string | number | null,
    string | number | null,
    string | number | null
  ];
}> = ({fees}) => {
  return (
    <>
      {/* <Typography mb={0.5}>
        <i>Tiered percentage fee:</i>
      </Typography> */}
      <Grid
        width="100%"
        flex={1}
        container
        spacing={1}
        borderBottom={`1px solid inline ${themeColors.greyLight}`}
      >
        <Grid
          // borderRight={`1px solid ${themeColors.grey}`}
          item
          boxSizing="border-box"
          width="25%"
          textAlign="center"
        >
          <Typography variant="overline">
            <i>0-500K:</i>
          </Typography>
          <Typography>
            <strong>{fees[0]}%</strong>
          </Typography>
        </Grid>
        <Grid
          // borderRight={`1px solid ${themeColors.grey}`}
          item
          boxSizing="border-box"
          width="25%"
          textAlign="center"
        >
          <Typography variant="overline">
            <i>500K-1M:</i>
          </Typography>
          <Typography>
            <strong>{fees[1]}%</strong>
          </Typography>
        </Grid>
        <Grid
          // borderRight={`1px solid ${themeColors.grey}`}
          item
          boxSizing="border-box"
          width="25%"
          textAlign="center"
        >
          <Typography variant="overline">
            <i>1M-2M:</i>
          </Typography>
          <Typography>
            <strong>{fees[2]}%</strong>
          </Typography>
        </Grid>
        <Grid item boxSizing="border-box" width="25%" textAlign="center">
          <Typography variant="overline">
            <i>2M+</i>
          </Typography>
          <Typography>
            <strong>{fees[3]}%</strong>
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

export const getPlanFeesServerFromFormik = (
  fields: planFeesFieldType | undefined
): string[] | null => {
  // TODO(nino)
  if (!fields) {
    return null;
  }
  const fees: string[] = [];
  if (fields.DFMFeeId) {
    fees.push(fields.DFMFeeId);
  }
  if (fields.FPFeeId) {
    fees.push(fields.FPFeeId);
  }
  if (fields.productFeeId) {
    fees.push(fields.productFeeId);
  }

  return fees;
};

export const defaultFeeFields: planFeesFieldType = {
  DFMFeeId: null,
  DFMFeeRequired: null,
  FPFeeId: null,
  FPFeeRequired: null,
  planServerFee: null,
  productFeeId: null,
  productFeeRequired: null,
  feeDeviation: false,
};

export const getPlanFeesFormikFromServer = <
  T extends Pick<OVPot, 'fees' | 'hasFeeDeviation' | 'pot'>
>(
  ovPot?: T | null
): planFeesFieldType | undefined => {
  const feeFields = clone(defaultFeeFields);
  if (!ovPot) {
    return undefined;
  }
  ovPot?.fees?.forEach((fee) => {
    if (fee.feeName?.name?.toLocaleLowerCase().includes('fp')) {
      feeFields.FPFeeId = fee.id;
      return;
    }
    if (fee.feeName?.name?.toLocaleLowerCase().includes('dfm')) {
      feeFields.DFMFeeId = fee.id;
      return;
    }
    // if (
    //   fee.feeName?.name?.toLocaleLowerCase().includes('product') || // used for GIA/ISA
    //   fee.feeName?.name?.toLocaleLowerCase().includes('sipp') || // used for sipp
    //   fee.feeName?.name?.toLocaleLowerCase().includes('platform') || // used for SPP GIA/ISA
    //   fee.feeType?.toLocaleLowerCase().includes('platform') // used for sipps
    // ) {
    feeFields.productFeeId = fee.id;
    // }
  });
  if (ovPot?.hasFeeDeviation) {
    feeFields.feeDeviation = true;
  }
  return feeFields;
};

export type planFeesFieldType = {
  planServerFee: string | null;
  DFMFeeId: string | null;
  FPFeeId: string | null;
  productFeeId: string | null;
  DFMFeeRequired: boolean | null;
  FPFeeRequired: boolean | null;
  productFeeRequired: boolean | null;
  feeDeviation: boolean | null;
};
